/**
 * The header scope control — ONE button, ONE popover, covering Organisation, Site and Plant.
 *
 * ── AND THE HEADER'S CRUMB, SINCE PROTOTYPE V2 (2026-11-02) ─────────────────
 * The header says where you are as a plain crumb, "Jazz / JAZZMSC5 / Alarms / Analytics", in every
 * look: this control's words (organisation, site, plant) with "/" between them, borderless until it
 * is hovered, focused or open, then the screen and the tab as plain text, then the site's state as
 * plain text. What follows is the history of the PICKER, which is unchanged: the one button and its
 * one popover, the columns, the keyboard. What the 2026-09 breadcrumb got wrong (three selects in a
 * box, three carets) is not what came back: one control, one caret, and text for the rest.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REPLACES THE BREADCRUMB, WHICH WAS REJECTED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The previous rework (`HeaderPath.tsx`, since deleted) drew `Organisation › Site › Plant` as
 * three inline `<select>`s in one bordered box. Rejected on sight: too wide, three carets, two
 * `›` separators, a gap after every label, and all three levels drawn at equal weight — it read
 * as three selects in a box, not as a path.
 *
 * This is the rework of the rework. The header shows ONE compact control:
 *
 *   ● Jamshoro · All plants  ⌄
 *
 * — the two facts an operator orients by at a glance, plus a single caret. Clicking anywhere on
 * it (not just the caret) opens one popover carrying all three levels, organisation included.
 * Changing organisation AND site now takes one open instead of two, because both selectors live
 * in the same surface.
 *
 * ── THE ORGANISATION IS IN THE BUTTON LABEL, WHEN THERE IS MORE THAN ONE ────
 * It used to be left out — read many times a minute, changing almost never, kept to the `title`,
 * the `aria-label` and the popover's first column. That made the switch invisible: a platform
 * administrator could not tell which organisation they were working in, nor that this button was
 * where it changed, and things were created in the wrong one (Hamzah, 2026-10-14). So when
 * `org.visible` (more than one organisation in reach) the label starts with it, muted, before the
 * site: "KSolar › Jamshoro". With one organisation, as for every tenant's own people, the label
 * is unchanged — a name with no alternative is noise.
 *
 * ── WHAT SURVIVES FROM THE BREADCRUMB, BYTE-FOR-BYTE ────────────────────────
 *   · The plant column is ABSENT, not empty, when the site has fewer than two recorded plants
 *     — `usePlantContext().visible`, unchanged.
 *   · `deviceIds: null` still means DO NOT NARROW; this file never reads that field, it only
 *     renders the picker that sets it (`app/plant-context-value.ts`).
 *   · The site's compliance-band dot stays — on the button, AND on each site row in the panel,
 *     because a picker that has to be opened to learn a site is failing is worse than the fleet
 *     map it is standing in for.
 *   · Switching organisation resets site, and switching site resets plant — both already live in
 *     `OrgProvider`/`SiteProvider`/`PlantProvider`; this control calls `.select()` and nothing
 *     else, exactly as the breadcrumb did.
 *   · Organisation options keep depth-first indentation (`orderedOrgs`, unchanged from the
 *     breadcrumb, formerly `ordered()` in the retired `OrgSelector.tsx` — `AccessView.tsx` cites
 *     this function by name and was updated in the same edit that moved it here the first time).
 *   · The "this page" / "fleet page" scope hint (`Docs/08-views/SITES.md` §2) is still reachable
 *     from the button's `title` — see `SCOPE_TITLE` below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * COLUMNS, NOT STACKED SECTIONS — the levels are nested, and the layout now says so
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A site is inside an organisation; a plant is inside a site. Stacking ORGANISATION / SITE /
 * PLANT as three vertical blocks (as this file did until 2026-09-21) drew three independent
 * axes and ran tall enough that a fleet with several orgs and several plants pushed the last
 * plant below the viewport. Columns fix both: containment is visible left-to-right, and each
 * column scrolls on its own instead of stretching the whole panel.
 *
 * **SITE ALWAYS RENDERS; the other two hide when they have no choice to offer.** That asymmetry
 * is the whole point of the layout and it was got wrong once, so the reasoning is written out:
 *
 *   · ORGANISATION renders when `org.visible` (more than one organisation in reach).
 *   · SITE renders whenever the panel is open at all — `siteColumnVisible` below.
 *   · PLANT renders when `plantVisible` (two or more recorded plants).
 *
 * ── WHY SITE DOES NOT TAKE THE HIDE RULE, THOUGH IT DID AT FIRST ─────────────
 *
 * The first version applied one rule to all three: a column appears only if that level has a
 * choice. For ORGANISATION and PLANT that is right — nothing to pick, nothing to draw. Applied
 * to SITE it produced this, on the only live fleet:
 *
 *     ORGANISATION │ PLANTS
 *     Thunder      │ All plants
 *       KSolar     │ Plant 1 · 4 devices
 *                  │ Plant 2 · 2 devices
 *
 * Correct by the rule, and it says something false. **Plants belong to a SITE, not to an
 * organisation** — and with the middle column gone the panel draws them as though they hang off
 * KSolar directly. The columns exist to make containment visible left-to-right; a level that
 * silently drops out of the middle breaks exactly the thing they were built to show, and it
 * breaks it hardest in the case that looks tidiest.
 *
 * The second cost is stability. `siteCanAct` flips the instant a second site is onboarded, so
 * the panel would silently change from two columns to three under someone who had learned the
 * two-column shape — a layout that rearranges itself on inventory is a layout nobody can build
 * a habit on.
 *
 * So SITE renders even when its listbox holds exactly one already-ticked row. That row is not a
 * choice and it is not pretending to be one: it is the label of the thing the plants beside it
 * are inside, which is worth one narrow column of the panel's width. The cost of drawing it is
 * a column; the cost of hiding it is a reader concluding plants belong to organisations.
 *
 * `siteCanAct` survives, narrowed to the one question it is still right for: whether the SITE
 * level alone justifies the control EXISTING (an error or an empty organisation must keep the
 * organisation switcher reachable — see the note on it below). It no longer decides a column.
 *
 * So the panel is zero columns (control hidden entirely) or two to three.
 *
 * **Selecting inside a non-last column does NOT close the panel.** The whole point of columns is
 * that picking an organisation reveals its sites and picking a site reveals its plants, in the
 * same open view — closing on every click would mean reopening the popover to see what was
 * revealed, which defeats the layout. Only a selection inside the CURRENTLY LAST rendered column
 * closes and refocuses the trigger, exactly as every selection did in the stacked version. Which
 * column is "last" is read fresh at click time, so on a site with no plants, selecting the site
 * closes immediately — there is nothing further to reveal.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ACCESSIBILITY — a native `<select>` gives this away for free; a popover does not
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The stacked version modelled this as one flat `role="menu"` of `role="menuitemradio"`, because
 * arrow keys walked one list end to end. Columns are not one list — they are several independent,
 * same-time lists that happen to share a surface — so the roles changed with the structure:
 *
 *   · `aria-haspopup="dialog"` on the trigger, not `"menu"`. The popup is not itself a menu, a
 *     listbox, a tree or a grid — it is a small panel that CONTAINS up to three listboxes side by
 *     side, which is exactly what `dialog` is for among the ARIA 1.2 `aria-haspopup` tokens.
 *   · The panel is `role="group"` (a plain container, not a widget of its own) with
 *     `aria-label="Scope"`.
 *   · Each column with options to offer is its OWN `role="listbox"`, labelled ORGANISATION / SITE
 *     / the plant noun. A column with nothing to select — the SITE column naming an error or an
 *     empty organisation — is plain text with no `listbox` role and no options at all: a control
 *     that cannot act is hidden, not disabled, and that extends to "not even an empty listbox".
 *   · Options are `role="option"` with `aria-selected`, matching the listbox they sit in (the
 *     stacked version used `menuitemradio` + `aria-checked`, which was the right pairing for a
 *     `role="menu"` and is the wrong one now that the container is a listbox).
 *   · ROVING TABINDEX, still a SINGLE cell across the WHOLE panel, now addressed by (column, row)
 *     instead of a flat index — exactly one button anywhere in the panel has `tabIndex={0}` at a
 *     time. This is deliberately NOT "one tab stop per column": the stacked version's comment
 *     argued at length for a single Tab-reachable element so leaving the panel is ever only one
 *     Tab away, and that argument does not change shape just because the layout grew a second
 *     dimension. Up/Down (and Home/End) move the roving cell within the CURRENT column; Left/Right
 *     move it to the nearest column on that side that actually has options, skipping a message-only
 *     column since there is nothing in it to land on. Neither Left nor Right wraps — running off
 *     the first or last column with options is a no-op, not a jump to the other end, because
 *     "always Left" and "always Right" are different directions and wrapping row navigation is a
 *     minor convenience while wrapping COLUMN navigation is disorienting in a way a radial menu is
 *     not. Enter/Space need no handler: a real `<button>` already answers both.
 *   · Because only one option is ever Tab-reachable, TAB IS NEVER TRAPPED — it leaves the panel
 *     for whatever the next focusable element on the page is. `onBlur` on the panel closes it once
 *     focus has genuinely left (checked via `relatedTarget`), unchanged from the stacked version.
 *   · Escape closes and returns focus to the trigger button (`close()`). An outside click also
 *     closes, but does NOT force focus back, unchanged.
 *   · Focus moves into the panel on open — the first option of the FIRST column that has any,
 *     skipping a message-only leading column exactly as Left/Right does — and is restored to the
 *     button on Escape or on a selection that closes the panel.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/lumen/Icon';
import { useOrgContext } from '@/app/org-context-value';
import { orderedOrgs } from '@/app/org-order';
import { useSiteContext, type SiteScope } from '@/app/site-context-value';
import { usePlantContext } from '@/app/plant-context-value';
import { HealthMark } from '@/components/lumen/HealthMark';
import { useSiteLiveness } from '@/lib/use-site-liveness';
import { useNetworkShown } from '@/lib/overview-network';
import { usePageCrumb } from '@/lib/page-crumb';
import { useScopePicker } from '@/app/scope-picker';
import { SimulatedPill, SimulationBuilding } from './SimulatedPill';

const BAND_COLOUR: Record<string, string> = {
  pass: 'var(--success)',
  warn: 'var(--warn)',
  fail: 'var(--danger)',
};

/** What the button's tooltip says, per scope. Verbatim from the retired `SiteSelector`. */
const SCOPE_TITLE: Record<SiteScope, string> = {
  site: 'This page follows the active site. Where a fleet view exists it is one labelled toggle on the page.',
  fleet:
    'This page is a fleet view by nature. Changing the active site changes what the other pages show, not this one.',
  none: 'This page has no site meaning. The active site still scopes every site page you open next.',
};

/**
 * Per-column pixel width. Fixed rather than flexible: a flexible column reflows every other
 * column's width as its own content changes length, which makes the panel visibly resize while
 * an operator is reading it. Names still truncate (`.truncate`) and carry a `title`, same
 * trade-off the single-column panel already made at 250px for one section.
 */
const COLUMN_WIDTH: Record<'org' | 'site' | 'plant', number> = {
  org: 156,
  site: 132,
  plant: 176,
};

/** One selectable row inside a column. */
interface ScopeOption {
  key: string;
  selected: boolean;
  onSelect: () => void;
  content: ReactNode;
}

/** One column of the panel — a listbox, or (SITE only) a message with no options at all. */
interface ScopeColumn {
  key: 'org' | 'site' | 'plant';
  /** Visible heading text, `aria-hidden` — the `listbox`'s own `aria-label` announces the name. */
  heading: string;
  headingExtra?: ReactNode;
  options: ScopeOption[];
  /** Set only for SITE, only when it has nothing to select — see decisions (1) and (2) below. */
  message?: { text: string; tone: 'danger' | 'muted' } | undefined;
  wrapperTitle?: string;
  wrapperOpacity?: number;
}

export function HeaderScope({
  scope = 'site',
  plantInScope = true,
  onAddSite,
  page,
}: {
  scope?: SiteScope;
  plantInScope?: boolean;
  /**
   * The screen's name, from `nav.ts`'s labels, as the third crumb — "Jazz / JAZZMSC5 / Overview",
   * followed by the tab the page is on when its tab row names one (`lib/page-crumb.ts`).
   * In EVERY look since prototype v2 (2026-11-02: "the header is a plain crumb, org / site / screen /
   * tab"). It was Glass-only from 2026-10-26, Solid keeping the 2026-10-08 decision to name no page
   * in the bar; v2 settles the header once for both. Plain text, not a control, and the first thing
   * to go on a narrow screen (app.css, `.lm-crumb-page`).
   */
  page?: string | undefined;
  /**
   * Offered under "No sites in this organisation" — the one thing to do next from an empty fleet.
   * Absent when the reader may not create a site (`sites.create`): a button that fails is worse
   * than none.
   */
  onAddSite?: (() => void) | undefined;
}): ReactNode {
  const org = useOrgContext();
  const site = useSiteContext();
  /* A site that has stopped reporting says so on the picker itself, where every screen's reader
     looks first (`lib/site-liveness.ts`, Hamzah 2026-10-13). Night says nothing: it is not a fault. */
  const { liveness, health } = useSiteLiveness(site.selectedId);
  const networkShown = useNetworkShown();
  const pageTab = usePageCrumb();
  const notReporting = !networkShown && (liveness.kind === 'silent' || liveness.kind === 'not-storing');
  const plant = usePlantContext();

  /* The open flag is SHARED with the page head's title (`app/scope-picker.ts`, prototype v2): the
     title is the same picker, so it opens this panel, never a second one. `anchor` says where. */
  const open = useScopePicker((s) => s.open);
  const anchor = useScopePicker((s) => s.anchor);
  const titleEl = useScopePicker((s) => s.titleEl);
  const setPicker = useScopePicker((s) => s.setOpen);
  const setOpen = useCallback(
    (v: boolean | ((was: boolean) => boolean)): void => {
      const next = typeof v === 'function' ? v(useScopePicker.getState().open) : v;
      setPicker(next, next ? 'header' : undefined);
    },
    [setPicker],
  );
  const atTitle = open && anchor === 'title' && titleEl !== null;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [focusedCol, setFocusedCol] = useState(0);
  const [focusedRow, setFocusedRow] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  /** `[column][row]` — rebuilt from the ref callbacks on every render, same convention the
   *  stacked version used for its flat array. */
  const optionRefs = useRef<Array<Array<HTMLButtonElement | null>>>([]);

  /** Dismiss without forcing focus anywhere — the outside click or the Tab already did. */
  const dismiss = useCallback((): void => setOpen(false), [setOpen]);
  /** Dismiss AND return focus to the trigger — Escape, and a selection in the last column. The
      trigger is whichever opened it: the crumb's button, or the page's title. */
  const close = useCallback((): void => {
    const s = useScopePicker.getState();
    setOpen(false);
    (s.anchor === 'title' && s.titleEl !== null ? s.titleEl : buttonRef.current)?.focus();
  }, [setOpen]);

  /* Outside click and Escape, mirroring `AccountMenu.tsx`. A menu that only closes by
     re-clicking its own trigger is the classic way to trap someone behind an overlay. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const at = e.target as Node;
      /* The panel may sit in a portal under the title, and the title is a trigger in its own right:
         a press on either is not "outside". */
      if (rootRef.current?.contains(at) || panelRef.current?.contains(at)) return;
      if (useScopePicker.getState().titleEl?.contains(at)) return;
      dismiss();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, dismiss, close]);

  /* Focus moves into the panel on open — the FIRST option of the FIRST column that has any, not
     necessarily the current selection, so the entry point is predictable regardless of which
     column is longest or whether the leading column is a message. `requestAnimationFrame`
     because the panel has to be in the DOM before it can take focus. Also resets the roving cell
     to (0, 0) so a column skipped over on this open is not silently "remembered" as focused from
     a previous session. */
  useEffect(() => {
    if (!open) return;
    setFocusedCol(0);
    setFocusedRow(0);
    const id = requestAnimationFrame(() => {
      for (const col of optionRefs.current) {
        const first = col?.[0];
        if (first) {
          first.focus();
          return;
        }
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const orgRows = org.visible ? orderedOrgs(org.organisations) : [];

  const orgOptions: ScopeOption[] = orgRows.map(({ node, depth }) => ({
    key: `org:${node.id}`,
    selected: node.id === org.selectedId,
    onSelect: () => org.select(node.id),
    content: (
      <span className="truncate" style={{ paddingLeft: depth * 14 }} title={node.name}>
        {node.name}
      </span>
    ),
  }));

  /*
   * PICKING A SITE OPENS ITS PAGE (product decision, 2026-09-29, per
   * `Docs/08-views/SITES.md` §2's "the header selector governs site-scoped screens" — this
   * is the missing other half: it did not previously say where "governs" LEADS).
   *
   * Same trick `SiteHubView`'s own `openStudio` uses for the identical reason: "THE TAB LIVES
   * IN THE URL" (`App.tsx`), so a push + a synthetic `popstate` is the documented way to move
   * the shell without threading a callback down through `Shell.tsx` for one button — `/site-
   * detail` is already in `TABS`, and `SiteHubView` already reads the selected site from THIS
   * context, not from a prop this control would otherwise have to carry.
   *
   * Skipped when already on `/site-detail`: an operator deep in one site's Analytics tab who
   * switches site via this control should land on the SAME tab for the new site, not be
   * bounced back to the default — `siteId` alone re-renders the hub's content, and a second
   * push here would only cost a redundant history entry.
   */
  const openSitePage = (id: string): void => {
    site.select(id);
    if (typeof window !== 'undefined' && window.location.pathname !== '/site-detail') {
      window.history.pushState({}, '', '/site-detail');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const siteOptions: ScopeOption[] = site.selectable.map((s) => ({
    key: `site:${s.id}`,
    selected: s.id === site.selectedId,
    onSelect: () => openSitePage(s.id),
    content: (
      <span className="flex min-w-0 items-center gap-[6px]" title={s.name}>
        <span
          className="size-[6px] shrink-0 rounded-full"
          style={{ background: BAND_COLOUR[s.band] ?? 'var(--muted-fg)' }}
          aria-hidden
        />
        <span className="truncate">{s.name}</span>
        <SimulatedPill simulated={s.simulated} />
      </span>
    ),
  }));

  /* Absent, not empty, when the site records fewer than two plants — same rule the retired
     `PlantSelector` applied. */
  const plantVisible = !plant.loading && plant.visible;
  const plantOptions: ScopeOption[] = plantVisible
    ? [
        {
          key: 'plant:all',
          selected: plant.selectedId === null,
          onSelect: () => plant.select(null),
          content: <>All {plant.spec.plural.toLowerCase()}</>,
        },
        ...plant.grouping.groups.map((g) => ({
          key: `plant:${g.id}`,
          selected: g.id === plant.selectedId,
          onSelect: () => plant.select(g.id),
          content: (
            <span className="flex min-w-0 items-center gap-[4px]" title={g.label}>
              <span className="truncate">{g.label}</span>
              <span style={{ color: 'var(--muted-fg)', fontWeight: 500 }}>
                · {g.deviceIds.length} device{g.deviceIds.length === 1 ? '' : 's'}
                {plant.inventoryTruncated ? '+' : ''}
              </span>
            </span>
          ),
        })),
      ]
    : [];

  /*
   * ONLY LOADING STILL REPLACES THE WHOLE CONTROL.
   *
   * This used to read "the site is the anchor" and let `site.loading`, `site.error` AND
   * `site.selectable.length === 0` all replace the entire control, on the theory that the
   * control is meaningless without a site to be under — true of the retired `SiteSelector`,
   * which only ever drew sites while the organisation picker sat beside it as ITS OWN
   * component. It stopped being true the moment the organisation moved INSIDE this control:
   * an organisation with no sites (or a failed sites request) took the ORGANISATION switcher
   * down with it, and a person who had just switched into that organisation had no way to
   * switch back out. Reported live.
   *
   * `site.loading` alone keeps the full-control replacement — it is transient (the request
   * either resolves or fails within moments) and nobody can get stranded behind a skeleton,
   * because nothing has settled yet for them to be stuck IN. `site.error` and an empty
   * `site.selectable` now scope to the SITE COLUMN below: the button still opens, ORGANISATION
   * stays listed and selectable, and only the site column names the gap.
   */
  /* Said to the page head, whose title is this picker too: with nothing to pick it is plain text. The
     same rule as `canPick` below, worked out before the loading return so the hook always runs. */
  const pickable =
    !site.loading && (org.visible || plantVisible || site.error || site.selectable.length !== 1);
  useLayoutEffect(() => {
    useScopePicker.setState({ canPick: pickable });
  }, [pickable]);

  if (site.loading) {
    return (
      <div
        className="lm-scope lm-sk rounded-[var(--radius-sm)]"
        style={{ width: 180, height: 'var(--header-ctl-h)' }}
      />
    );
  }

  /* Two different diagnoses, kept apart rather than collapsed into one message: an empty
     organisation is a settled answer, a failed request is not an answer at all — the same
     "say which of no-data and old-data you mean" rule Overview's reporting states follow. */
  const siteEmpty = !site.error && site.selectable.length === 0;

  /*
   * THE GENERALISED HIDE RULE. `org.visible` and `plantVisible` already hide their OWN
   * columns when there is nothing to choose (one organisation; fewer than two plants). This
   * extends the same idea to the control as a whole: it renders if ANY column can act, and is
   * hidden only when NONE can — one organisation, one settled site with no gap to name, and
   * fewer than two plants. An empty or failing site list is not "inert": it is information the
   * operator needs, and (more importantly) it is exactly the state that must keep ORGANISATION
   * reachable, so it counts as "can act" here even though nothing in the site column itself is
   * selectable.
   *
   * IT DOES NOT DECIDE WHETHER SITE GETS A COLUMN. It used to, and the header block records what
   * that produced. This boolean answers "is there any reason for this control to exist?"; the
   * SITE column answers "what is the plant column inside?", and a single settled site is a poor
   * answer to the first and the only answer to the second.
   */
  const siteCanAct = site.error || siteEmpty || site.selectable.length > 1;
  /*
   * WITH NOTHING TO CHOOSE THE CRUMB STAYS, AS TEXT (prototype v2, 2026-11-02). This returned null
   * when no level could act — one organisation, one site, no plants — which was right while the
   * control was only a picker. It is the header's crumb now, and "where am I" has an answer even
   * when there is no choice to offer: the same words, plain, with no button and no caret.
   */
  const canPick = org.visible || plantVisible || siteCanAct;
  /* Under the title, the panel is placed from the title's own box (a portal, so no card clips it). */
  const titleBox = atTitle ? titleEl.getBoundingClientRect() : null;

  const orgName = org.selected?.name;
  /* While the Overview shows the network (2026-10-26) no site is what the page shows, so the scope
     names none: "Thunder Energy / Network", with no site's pills and no page crumb after it. */
  const siteName = site.error
    ? 'Sites unavailable'
    : siteEmpty
      ? 'No sites'
      : networkShown
        ? 'Network'
        : (site.selected?.name ?? 'Select site');
  const siteNameColour = site.error
    ? 'var(--danger-ink)'
    : scope === 'none'
      ? 'var(--muted-fg)'
      : 'var(--fg)';
  const plantPhrase = plantVisible && !networkShown
    ? plant.selectedId === null
      ? `All ${plant.spec.plural.toLowerCase()}`
      : (plant.selected?.label ?? plant.spec.noun)
    : undefined;

  /* The organisation is never IN the label, but it must never be UNKNOWABLE — it lives here and
     in `title` below, reachable in one hover or one screen-reader step. */
  const ariaLabel = `Scope — ${orgName ? `${orgName}, ` : ''}${siteName}${
    plantPhrase ? `, ${plantPhrase.toLowerCase()}` : ''
  }${notReporting ? ', not reporting' : ''}`;

  const notInScopeNote =
    plantVisible && !plantInScope
      ? ` A ${plant.spec.noun.toLowerCase()} does not narrow this screen — the selection still applies to the ones that follow it.`
      : '';
  const title = `${orgName ? `${orgName} · ` : ''}${siteName}${
    plantPhrase ? ` · ${plantPhrase}` : ''
  }. ${SCOPE_TITLE[scope]}${notInScopeNote}`;

  /* ---------------------------------------------------------------- the columns themselves */
  const columns: ScopeColumn[] = [];

  /* One organisation is not a choice — absent, not disabled, exactly as `OrgSelector` rendered
     nothing until a caller reached more than one. */
  if (orgOptions.length > 0) {
    columns.push({ key: 'org', heading: 'Organisation', options: orgOptions });
  }

  /*
   * SITE IS THE ONE COLUMN WITH NO CONDITION ON IT, and the missing `if` is the decision rather
   * than an omission — the header block says why at length. In short: this column used to be
   * gated on `siteCanAct` like its two neighbours, and on the live fleet that collapsed the
   * panel to ORGANISATION │ PLANTS, drawing plants as though they hang off an organisation.
   * They hang off a SITE, and this column is the only thing on screen that says so.
   *
   * It therefore renders for a single already-ticked site too. That row is not offered as a
   * choice; it is the label of the thing the column beside it is inside.
   *
   * `siteCanAct` above is still the right question for whether the CONTROL exists at all. It is
   * not the right question for this.
   */
  columns.push({
    key: 'site',
    heading: 'Site',
    wrapperTitle: SCOPE_TITLE[scope],
    options: site.error || siteEmpty ? [] : siteOptions,
    message: site.error
      ? /* Naming the gap in one line, not a disabled list — a control that cannot act is
           hidden, not greyed out (GUIDELINES §2). */
        { text: 'Sites unavailable — try again shortly', tone: 'danger' }
      : siteEmpty
        ? { text: 'No sites in this organisation', tone: 'muted' }
        : undefined,
  });

  if (plantVisible) {
    columns.push({
      key: 'plant',
      heading: plant.spec.plural,
      /* A short inventory is MARKED, not silently rendered as a number — see the row counts,
         which carry a trailing `+`, and this icon's tooltip. */
      headingExtra: plant.inventoryTruncated && (
        <span
          style={{ color: 'var(--warn-ink)', lineHeight: 0 }}
          title="The device inventory stopped short of the end, so these counts are low and a device missing from it will appear under every group."
        >
          <Icon name="ic-alert" size={11} />
        </span>
      ),
      wrapperOpacity: plantInScope ? 1 : 0.72,
      wrapperTitle:
        notInScopeNote ||
        `${plant.spec.why}${
          plant.grouping.ungroupedDeviceIds.length > 0
            ? ` ${plant.grouping.ungroupedDeviceIds.length} device(s) serve the whole site and belong to no ${plant.spec.noun.toLowerCase()}.`
            : ''
        }`,
      options: plantOptions,
    });
  }

  /* Clamp against the CURRENT render's shape rather than trusting the state as stored — an
     async change (an organisation switch mid-panel, say) can shrink `columns` between the
     render that set the roving cell and this one, and an out-of-range cell would mean no button
     anywhere claims `tabIndex={0}`, which breaks Tab into the panel entirely. */
  const clampedCol = Math.min(focusedCol, Math.max(columns.length - 1, 0));
  const clampedRow = columns[clampedCol]
    ? Math.min(focusedRow, Math.max(columns[clampedCol].options.length - 1, 0))
    : 0;

  const focusCell = (col: number, row: number): void => {
    setFocusedCol(col);
    setFocusedRow(row);
    optionRefs.current[col]?.[row]?.focus();
  };

  /* Up/Down/Home/End move the roving cell within the CURRENT column. Left/Right move it to the
     nearest column on that side that has options, skipping a message-only column since there is
     nothing there to land on. Neither Left nor Right wraps — see the file comment for why that
     asymmetry with the (also non-wrapping, but symmetric-feeling) row navigation is deliberate. */
  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (columns.length === 0) return;
    const count = columns[clampedCol]?.options.length ?? 0;
    if (e.key === 'ArrowDown' && count > 0) {
      e.preventDefault();
      focusCell(clampedCol, (clampedRow + 1) % count);
    } else if (e.key === 'ArrowUp' && count > 0) {
      e.preventDefault();
      focusCell(clampedCol, (clampedRow - 1 + count) % count);
    } else if (e.key === 'Home' && count > 0) {
      e.preventDefault();
      focusCell(clampedCol, 0);
    } else if (e.key === 'End' && count > 0) {
      e.preventDefault();
      focusCell(clampedCol, count - 1);
    } else if (e.key === 'ArrowLeft') {
      for (let c = clampedCol - 1; c >= 0; c--) {
        const target = columns[c];
        if (target && target.options.length > 0) {
          e.preventDefault();
          focusCell(c, Math.min(clampedRow, target.options.length - 1));
          break;
        }
      }
    } else if (e.key === 'ArrowRight') {
      for (let c = clampedCol + 1; c < columns.length; c++) {
        const target = columns[c];
        if (target && target.options.length > 0) {
          e.preventDefault();
          focusCell(c, Math.min(clampedRow, target.options.length - 1));
          break;
        }
      }
    }
    /* Enter and Space are NOT handled here — the focused element is a real `<button>`, and a
       real button answers both on its own. Adding a handler here would fire the select twice. */
  };

  /* Tab is never trapped (roving tabindex means only one option is ever in the Tab order), so
     once focus genuinely leaves the panel the panel should stop claiming to be open. */
  const onPanelBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) dismiss();
  };

  const renderOption = (colIdx: number, opt: ScopeOption, rowIdx: number): ReactNode => (
    <button
      key={opt.key}
      ref={(el) => {
        (optionRefs.current[colIdx] ??= [])[rowIdx] = el;
      }}
      type="button"
      role="option"
      aria-selected={opt.selected}
      tabIndex={colIdx === clampedCol && rowIdx === clampedRow ? 0 : -1}
      onFocus={() => {
        setFocusedCol(colIdx);
        setFocusedRow(rowIdx);
      }}
      onClick={() => {
        opt.onSelect();
        setFocusedCol(colIdx);
        setFocusedRow(rowIdx);
        /* Selecting inside the LAST rendered column is terminal — there is nothing further to
           reveal, so close and hand focus back to the trigger, exactly as every selection did
           before columns existed. Selecting anywhere earlier reveals the column(s) to its right
           and stays open so the operator can see and use what just appeared. */
        if (colIdx === columns.length - 1) close();
      }}
      className="lm-hov flex w-full items-center justify-between gap-[10px] rounded-[var(--radius-sm)] px-[10px] py-[6px] text-left text-[12.5px] font-semibold outline-none"
      style={{ color: 'var(--fg)' }}
    >
      {opt.content}
      {opt.selected && (
        <span style={{ color: 'var(--brand)', display: 'flex', flex: 'none' }} aria-hidden>
          <Icon name="ic-check" size={13} />
        </span>
      )}
    </button>
  );

  /*
   * THE CRUMB'S WORDS: the organisation, then the site and the plant. One set of spans, drawn
   * inside the picker's button when there is a choice to make and as plain text when there is not,
   * so the crumb reads the same either way. The organisation is named whenever it is known (v2:
   * "Jazz / JAZZMSC5 / Overview"); with one organisation it is plain text before the button, since
   * there is nothing about it to pick.
   */
  const orgCrumb = (inside: boolean): ReactNode =>
    orgName !== undefined && (
      <>
        <span className="lm-scope-org lm-crumb-org min-w-0 truncate">{orgName}</span>
        <span className={`lm-scope-org lm-crumb-sep${inside ? '' : ' lm-crumb-sep-out'}`} aria-hidden>
          /
        </span>
      </>
    );
  const siteWords = (
    <>
      {site.error ? (
        /* The alert glyph that used to mark the whole-control replacement now marks the
           button instead — the state moved from "instead of the control" to "on it". */
        <span className="flex flex-none text-[var(--danger-ink)]" aria-hidden>
          <Icon name="ic-alert" size={12} />
        </span>
      ) : (
        site.selected &&
        !networkShown && (
          /* The compliance band's dot. Not on a phone (under 640 px): there the site's name and its
             state are what must fit, and the button's title still names the band's site. */
          <span
            className="hidden size-[6px] shrink-0 rounded-full sm:block"
            style={{ background: BAND_COLOUR[site.selected.band] ?? 'var(--muted-fg)' }}
            aria-hidden
          />
        )
      )}
      <span className="lm-crumb-site min-w-0 truncate" style={{ color: siteNameColour }}>
        {siteName}
      </span>
      {plantPhrase && (
        <span className={`lm-crumb-plant min-w-0 truncate${plantInScope ? '' : ' lm-crumb-plant-out'}`}>
          · {plantPhrase}
        </span>
      )}
    </>
  );
  /* The tab the page is on, published by its tab row (`lib/page-crumb.ts`): the fourth crumb. */
  const crumbs = page !== undefined && !networkShown && (
    <>
      <span className="lm-crumb-page">
        <span aria-hidden className="lm-crumb-sep">
          /
        </span>
        <span className="min-w-0 truncate">{page}</span>
      </span>
      {pageTab !== null && pageTab !== page && (
        <span className="lm-crumb-page lm-crumb-tab">
          <span aria-hidden className="lm-crumb-sep">
            /
          </span>
          <span className="min-w-0 truncate">{pageTab}</span>
        </span>
      )}
    </>
  );

  /*
   * THE CRUMB GIVES WAY, NEVER THE CONTROLS AFTER IT (coordinator's check at 390 x 844, 2026-11-02:
   * with this box `flex: none`, "Network ⌄" kept its 98 px, the NOC wall and the alarm timeline were
   * squashed to 16 px each and the account menu sat off the screen, the only way to Appearance and
   * Sign out). The box now shrinks (`.lm-topbar-tools > .lm-scope`, app.css) and its words sit in
   * `.lm-scope-row`, which clips: the site's name ends in an ellipsis first, and nothing in the crumb
   * can paint over the buttons beside it. The popover stays outside the clipped row, so it is never cut.
   */
  return (
    <div ref={rootRef} className="lm-scope relative flex min-w-0 items-center">
      <div className="lm-scope-row">
        {!org.visible && orgCrumb(false)}
        {canPick ? (
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={ariaLabel}
            title={title}
            className="lm-scope-btn lm-hov flex min-w-0 items-center gap-[6px] rounded-[var(--radius-sm)] pl-[8px] pr-[6px] outline-none"
          >
            {org.visible && orgCrumb(true)}
            {siteWords}
            <span className="flex flex-none text-[var(--muted-fg)]" aria-hidden>
              <Icon name={open ? 'ic-chevup' : 'ic-chevdown'} size={13} />
            </span>
          </button>
        ) : (
          <span className="lm-scope-text flex min-w-0 items-center gap-[6px]" title={title}>
            {siteWords}
          </span>
        )}
        {crumbs}
        {/*
          * THE SITE'S STATE, AS PLAIN TEXT AFTER THE CRUMB (v2: "the site's state as plain text").
          * It sat inside the picker's button, so the button read as a capsule of capsules; out here it
          * is a word with its dot (never colour alone), and the button holds only names. A simulated
          * site keeps its marker: a state, which is what a pill is still for.
          */}
        {!networkShown && <SimulatedPill simulated={site.selected?.simulated} />}
        {!networkShown && <SimulationBuilding sim={health?.simulation} />}
        {notReporting && (
          /* On a phone the word is read, not shown: the mark is a filled disc, a shape of its own
             (`HealthMark`: never colour alone), and the word stays its accessible name and title. */
          <span className="lm-crumb-state" title={liveness.kind === 'not-storing' ? 'Not storing' : 'Not reporting'}>
            <HealthMark tone="fail" size={7} />
            <span className="sr-only sm:not-sr-only">{liveness.kind === 'not-storing' ? 'Not storing' : 'Not reporting'}</span>
          </span>
        )}
      </div>

      {open && (atTitle ? (p: ReactNode) => createPortal(p, document.body) : (p: ReactNode) => p)(
        <div
          ref={panelRef}
          role="group"
          aria-label="Scope"
          className={`lm-overlay z-50 flex items-stretch ${titleBox ? 'fixed' : 'absolute left-0'}`}
          style={{
            ...(titleBox
              ? { left: titleBox.left, top: titleBox.bottom + 6 }
              : { top: 'calc(100% + 6px)' }),
            maxWidth: 'calc(100vw / var(--app-zoom, 1) - 24px)',
            overflow: 'hidden',
          }}
          onKeyDown={onPanelKeyDown}
          onBlur={onPanelBlur}
        >
          {columns.map((col, colIdx) => (
            <div
              key={col.key}
              role={col.options.length > 0 ? 'listbox' : undefined}
              aria-label={col.options.length > 0 ? col.heading : undefined}
              className="overflow-y-auto p-[6px_0]"
              style={{
                width: COLUMN_WIDTH[col.key],
                flex: 'none',
                maxHeight: 360,
                borderLeft: colIdx > 0 ? '1px solid var(--border)' : undefined,
                opacity: col.wrapperOpacity ?? 1,
              }}
              title={col.wrapperTitle}
            >
              <div
                aria-hidden
                className="flex items-center gap-[5px] px-[10px] pb-[3px] pt-[7px] text-[12px] font-semibold"
                style={{ color: 'var(--muted-fg)' }}
              >
                <span>{col.heading}</span>
                {col.headingExtra}
              </div>
              {col.message ? (
                <div
                  className="px-[10px] py-[6px] text-[12.5px]"
                  style={{
                    color: col.message.tone === 'danger' ? 'var(--danger-ink)' : 'var(--muted-fg)',
                  }}
                >
                  {col.message.text}
                  {col.key === 'site' && siteEmpty && onAddSite && (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        onAddSite();
                      }}
                      className="lm-hov mt-[6px] block rounded-[var(--radius-sm)] border border-[var(--border)] px-[9px] py-[4px] text-[12px] font-semibold text-[var(--fg)]"
                    >
                      Add a site
                    </button>
                  )}
                </div>
              ) : (
                col.options.map((opt, rowIdx) => renderOption(colIdx, opt, rowIdx))
              )}
            </div>
          ))}
        </div>,
      )}
    </div>
  );
}
